import * as i0 from '@angular/core';
import { InjectionToken, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, ViewChild, HostBinding, HostListener, Input, Injector, Injectable, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$3 from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';
import * as i1 from '@angular/cdk/portal';
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { IconModule } from '@coyoapp/ui/icon';
import * as i1$1 from '@coyoapp/ui/button';
import { ButtonModule } from '@coyoapp/ui/button';
import * as i1$2 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import { CUI_SELECT_CONTAINER_CONFIG } from '@coyoapp/ui/select';
import { Subject } from 'rxjs';

var DialogWidth;
(function (DialogWidth) {
    DialogWidth["SMALL"] = "sm";
    DialogWidth["MEDIUM"] = "md";
    DialogWidth["LARGE"] = "lg";
})(DialogWidth || (DialogWidth = {}));

const DialogOptions = new InjectionToken('cui-dialog-options');

const DialogReference = new InjectionToken('cui-dialog-ref');
/**
 * Reference to the currently opened dialog. Can be injected by the dialog component via {@link DialogReference}.
 */
class CuiDialogReference {
    constructor(closingSubject, overlayRef, data) {
        this.closingSubject = closingSubject;
        this.overlayRef = overlayRef;
        this.data = data;
        this.result = closingSubject.asObservable();
    }
    /**
     * Closes the dialog with the given result. Result is optional, if not given the result observable will just complete.
     *
     * @param result - the result of the dialog
     */
    close(result) {
        this.overlayRef.dispose();
        if (result !== undefined) {
            this.closingSubject.next(result);
        }
        this.closingSubject.complete();
    }
}

class DialogPortalComponent extends BasePortalOutlet {
    constructor(dialogOptions, dialogReference) {
        super();
        this.dialogOptions = dialogOptions;
        this.dialogReference = dialogReference;
        this.sizeClass = DialogWidth.MEDIUM;
        this.hostClass = true;
        this.sizeClass = this.dialogOptions.width || DialogWidth.MEDIUM;
    }
    attachComponentPortal(portal) {
        return this._portalOutlet.attachComponentPortal(portal);
    }
    attachTemplatePortal(portal) {
        return this._portalOutlet.attachTemplatePortal(portal);
    }
    close() {
        this.dialogReference.close();
    }
}
DialogPortalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogPortalComponent, deps: [{ token: DialogOptions }, { token: DialogReference }], target: i0.ɵɵFactoryTarget.Component });
DialogPortalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: DialogPortalComponent, selector: "cui-dialog-portal", host: { listeners: { "document:keydown.esc": "close()" }, properties: { "class": "this.sizeClass", "class.cui-dialog-portal": "this.hostClass" } }, viewQueries: [{ propertyName: "_portalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<ng-template cdkPortalOutlet></ng-template>\n", styles: [".cui-dialog-portal{position:relative;margin:1.5rem;background-color:var(--cui-dialog-bg-color, #ffffff);border-radius:var(--cui-border-radius-l, .5rem);box-shadow:0 2px 4px -1px #101d300d,0 9px 16px -3px #101d3024,0 32px 20px -20px #101d3033}.cui-dialog-portal.lg{width:var(--cui-dialog-width-lg, 960px)}.cui-dialog-portal.md{width:var(--cui-dialog-width-md, 640px)}.cui-dialog-portal.sm{width:var(--cui-dialog-width-sm, 400px)}\n"], directives: [{ type: i1.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogPortalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cui-dialog-portal', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<ng-template cdkPortalOutlet></ng-template>\n", styles: [".cui-dialog-portal{position:relative;margin:1.5rem;background-color:var(--cui-dialog-bg-color, #ffffff);border-radius:var(--cui-border-radius-l, .5rem);box-shadow:0 2px 4px -1px #101d300d,0 9px 16px -3px #101d3024,0 32px 20px -20px #101d3033}.cui-dialog-portal.lg{width:var(--cui-dialog-width-lg, 960px)}.cui-dialog-portal.md{width:var(--cui-dialog-width-md, 640px)}.cui-dialog-portal.sm{width:var(--cui-dialog-width-sm, 400px)}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DialogOptions]
                }] }, { type: CuiDialogReference, decorators: [{
                    type: Inject,
                    args: [DialogReference]
                }] }]; }, propDecorators: { _portalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }], sizeClass: [{
                type: HostBinding,
                args: ['class']
            }], hostClass: [{
                type: HostBinding,
                args: ['class.cui-dialog-portal']
            }], close: [{
                type: HostListener,
                args: ['document:keydown.esc']
            }] } });

const CUI_TOAST_DEFAULT_CONFIG = {
    i18n: {
        close: 'Close'
    },
    defaults: {
        width: DialogWidth.MEDIUM
    }
};
const CUI_DIALOG_CONFIG = new InjectionToken('CUI_DIALOG_CONFIG', {
    factory: () => CUI_TOAST_DEFAULT_CONFIG
});

/**
 * Component for rendering a title of a dialog
 */
class DialogTitleWrapperComponent {
    constructor(dialogReference, config) {
        this.dialogReference = dialogReference;
        this.config = config;
        /**
         * The title of the dialog
         */
        this.title = '';
        /**
         * Flag if the dialog should be closable
         */
        this.closable = true;
        /**
         * The id of the dialog which is attached to the title for a11y labeling
         */
        this.dialogTitleId = '';
        this.closeAriaLabel = config.i18n.close;
    }
    onClick() {
        this.dialogReference.close();
    }
}
DialogTitleWrapperComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogTitleWrapperComponent, deps: [{ token: DialogReference }, { token: CUI_DIALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Component });
DialogTitleWrapperComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: DialogTitleWrapperComponent, selector: "cui-dialog-title-wrapper", inputs: { title: "title", closable: "closable", dialogTitleId: "dialogTitleId" }, host: { classAttribute: "cui-dialog-title" }, ngImport: i0, template: "<div class=\"title-row\">\n  <h3 *ngIf=\"title\" id=\"{{dialogTitleId}}\" class=\"cui-h3\">{{title}}</h3>\n  <button *ngIf=\"closable\"\n          cui-icon-button\n          icon=\"close\"\n          mode=\"ghost\"\n          type=\"button\"\n          size=\"s\"\n          class=\"cui-dialog-close\"\n          (click)=\"onClick()\"\n          [attr.aria-label]=\"closeAriaLabel\"></button>\n</div>\n<ng-content></ng-content>\n", styles: [".cui-dialog-title{display:flex;flex-direction:column;padding:1.25rem 1.5rem;word-wrap:break-word;word-break:break-word}.cui-dialog-title h3{margin:0;align-self:center;width:100%}.cui-dialog-title .title-row{display:flex;align-items:flex-start;justify-content:flex-end}.cui-dialog-title>*{flex:1 1 auto}\n"], components: [{ type: i1$1.ButtonComponent, selector: "button[cui-button], button[cui-icon-button]", inputs: ["mode", "look", "size", "round", "loading", "disabled", "prefixIcon", "suffixIcon", "textClass", "iconClass"] }], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.IconButtonDirective, selector: "button[cui-icon-button], a[cui-icon-button]", inputs: ["icon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogTitleWrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cui-dialog-title-wrapper', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                        class: 'cui-dialog-title'
                    }, template: "<div class=\"title-row\">\n  <h3 *ngIf=\"title\" id=\"{{dialogTitleId}}\" class=\"cui-h3\">{{title}}</h3>\n  <button *ngIf=\"closable\"\n          cui-icon-button\n          icon=\"close\"\n          mode=\"ghost\"\n          type=\"button\"\n          size=\"s\"\n          class=\"cui-dialog-close\"\n          (click)=\"onClick()\"\n          [attr.aria-label]=\"closeAriaLabel\"></button>\n</div>\n<ng-content></ng-content>\n", styles: [".cui-dialog-title{display:flex;flex-direction:column;padding:1.25rem 1.5rem;word-wrap:break-word;word-break:break-word}.cui-dialog-title h3{margin:0;align-self:center;width:100%}.cui-dialog-title .title-row{display:flex;align-items:flex-start;justify-content:flex-end}.cui-dialog-title>*{flex:1 1 auto}\n"] }]
        }], ctorParameters: function () { return [{ type: CuiDialogReference, decorators: [{
                    type: Inject,
                    args: [DialogReference]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CUI_DIALOG_CONFIG]
                }] }]; }, propDecorators: { title: [{
                type: Input
            }], closable: [{
                type: Input
            }], dialogTitleId: [{
                type: Input
            }] } });

/**
 * Component for rendering dialog action buttons.
 *
 * @example
 * ```
 * <cui-dialog-actions>
 *   <button cui-button>No</button>
 *   <button cui-button>Yes</button>
 * </cui-dialog-actions>
 * ```
 */
class DialogActionsComponent {
}
DialogActionsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogActionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DialogActionsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: DialogActionsComponent, selector: "cui-dialog-actions", host: { classAttribute: "cui-dialog-actions" }, ngImport: i0, template: "<ng-content></ng-content>\n", styles: [".cui-dialog-actions{display:flex;justify-content:flex-end;flex-direction:row;flex-wrap:wrap;padding:.25rem 1.5rem 1.25rem}.cui-dialog-actions button{margin-top:1rem;margin-left:.5rem}.cui-dialog-actions button:first-child{margin-left:0}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogActionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cui-dialog-actions', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                        class: 'cui-dialog-actions'
                    }, template: "<ng-content></ng-content>\n", styles: [".cui-dialog-actions{display:flex;justify-content:flex-end;flex-direction:row;flex-wrap:wrap;padding:.25rem 1.5rem 1.25rem}.cui-dialog-actions button{margin-top:1rem;margin-left:.5rem}.cui-dialog-actions button:first-child{margin-left:0}\n"] }]
        }] });

let nextUniqueId = 0;
/**
 * Dialog Component should wrap the the title, content and actions of a dialog.
 *
 * @example
 * Here is a simple example of a confirmation dialog
 * ```
 * <cui-dialog>
 *  <cui-dialog-title title="Test title" [closable]="true"></cui-dialog-title>
 *  Are you sure you want to do this?
 *  <cui-dialog-actions>
 *    <button cui-button>No</button>
 *    <button cui-button>Yes</button>
 *  </cui-dialog-actions>
 * </cui-dialog>
 * ```
 */
class DialogComponent {
    constructor(focusTrapFactory, elementRef) {
        this.focusTrapFactory = focusTrapFactory;
        this.elementRef = elementRef;
        this.titleId = `dialog-title-${nextUniqueId++}`;
        /**
         * The title of the dialog.
         */
        this.title = '';
        /**
         * Autofocus the element by the given query selector or the first element in
         * the dialog.
         */
        this.autoFocus = false;
        /**
         * Flag if the dialog should be closable.
         */
        this.closable = true;
    }
    calculateElevations() {
        const scrollContainer = this.scrollContainerElementRef.nativeElement;
        if (scrollContainer.scrollTop) {
            scrollContainer.classList.add('elevation-top');
        }
        else {
            scrollContainer.classList.remove('elevation-top');
        }
        if (scrollContainer.scrollTop + scrollContainer.clientHeight < scrollContainer.scrollHeight) {
            scrollContainer.classList.add('elevation-bottom');
        }
        else {
            scrollContainer.classList.remove('elevation-bottom');
        }
    }
    ngAfterViewInit() {
        this.calculateElevations();
        this.setupFocusTrap();
    }
    ngOnDestroy() {
        this.focusTrap?.destroy();
    }
    setupFocusTrap() {
        this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
        if (this.autoFocus) {
            if (typeof this.autoFocus === 'string') {
                this.elementRef.nativeElement.querySelector(this.autoFocus)?.focus();
            }
            else {
                this.focusTrap.focusFirstTabbableElementWhenReady();
            }
        }
    }
}
DialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogComponent, deps: [{ token: i1$2.ConfigurableFocusTrapFactory }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: DialogComponent, selector: "cui-dialog", inputs: { title: "title", autoFocus: "autoFocus", closable: "closable" }, host: { classAttribute: "cui-dialog" }, providers: [{
            provide: CUI_SELECT_CONTAINER_CONFIG,
            useValue: {
                containerSelector: '.cui-dialog-portal'
            }
        }], viewQueries: [{ propertyName: "scrollContainerElementRef", first: true, predicate: ["scrollContainer"], descendants: true, static: true }], ngImport: i0, template: "<div role=\"dialog\" aria-modal=\"true\" [attr.aria-labelledby]=\"titleId\" cdkTrapFocus class=\"cui-dialog-focus-trap\">\n  <cui-dialog-title-wrapper [title]=\"title\" [closable]=\"closable\" [dialogTitleId]=\"titleId\">\n    <ng-content select=\"cui-dialog-title\"></ng-content>\n  </cui-dialog-title-wrapper>\n  <div class=\"cui-dialog-content-scroll-container\" (scroll)=\"calculateElevations()\" #scrollContainer>\n    <div class=\"cui-dialog-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n  <ng-content select=\"cui-dialog-actions\"></ng-content>\n</div>\n", styles: [".cui-dialog{display:flex;flex-direction:column;max-height:100%;overflow:hidden}@media (forced-colors: active){.cui-dialog{outline:1px solid ButtonBorder}}.cui-dialog .cui-dialog-focus-trap{display:flex;flex-direction:column;max-height:100%;overflow:hidden}.cui-dialog .cui-dialog-content-scroll-container{overflow:auto;max-height:100%}.cui-dialog .cui-dialog-content{padding:0 1.5rem}.cui-dialog .elevation-top{box-shadow:inset 0 5px 5px -1px #101d301f}.cui-dialog .elevation-bottom{box-shadow:inset 0 -5px 5px -1px #101d301f}.cui-dialog .elevation-bottom.elevation-top{box-shadow:inset 0 5px 5px -1px #101d301f,inset 0 -5px 5px -1px #101d301f}\n"], components: [{ type: DialogTitleWrapperComponent, selector: "cui-dialog-title-wrapper", inputs: ["title", "closable", "dialogTitleId"] }], directives: [{ type: i1$2.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cui-dialog', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [{
                            provide: CUI_SELECT_CONTAINER_CONFIG,
                            useValue: {
                                containerSelector: '.cui-dialog-portal'
                            }
                        }], host: {
                        class: 'cui-dialog'
                    }, template: "<div role=\"dialog\" aria-modal=\"true\" [attr.aria-labelledby]=\"titleId\" cdkTrapFocus class=\"cui-dialog-focus-trap\">\n  <cui-dialog-title-wrapper [title]=\"title\" [closable]=\"closable\" [dialogTitleId]=\"titleId\">\n    <ng-content select=\"cui-dialog-title\"></ng-content>\n  </cui-dialog-title-wrapper>\n  <div class=\"cui-dialog-content-scroll-container\" (scroll)=\"calculateElevations()\" #scrollContainer>\n    <div class=\"cui-dialog-content\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n  <ng-content select=\"cui-dialog-actions\"></ng-content>\n</div>\n", styles: [".cui-dialog{display:flex;flex-direction:column;max-height:100%;overflow:hidden}@media (forced-colors: active){.cui-dialog{outline:1px solid ButtonBorder}}.cui-dialog .cui-dialog-focus-trap{display:flex;flex-direction:column;max-height:100%;overflow:hidden}.cui-dialog .cui-dialog-content-scroll-container{overflow:auto;max-height:100%}.cui-dialog .cui-dialog-content{padding:0 1.5rem}.cui-dialog .elevation-top{box-shadow:inset 0 5px 5px -1px #101d301f}.cui-dialog .elevation-bottom{box-shadow:inset 0 -5px 5px -1px #101d301f}.cui-dialog .elevation-bottom.elevation-top{box-shadow:inset 0 5px 5px -1px #101d301f,inset 0 -5px 5px -1px #101d301f}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.ConfigurableFocusTrapFactory }, { type: i0.ElementRef }]; }, propDecorators: { scrollContainerElementRef: [{
                type: ViewChild,
                args: ['scrollContainer', { static: true }]
            }], title: [{
                type: Input
            }], autoFocus: [{
                type: Input
            }], closable: [{
                type: Input
            }] } });

const OverlayReference = new InjectionToken('cui-overlay-ref');

/**
 * Dialog service is responsible for every dialog related operation.
 */
class DialogService {
    constructor(overlay, injector, config) {
        this.overlay = overlay;
        this.injector = injector;
        this.config = config;
    }
    /**
     * Opens a cui dialog. For operations like closing the dialog please inject {@link DialogReference} in your dialog
     * component. Overlay will close on backdrop click and escape key.
     *
     * @param componentType - The component type to render inside the dialog.The component must use
     * {@link DialogComponent} for consistent and correct rendering of the {@link DialogActionsComponent} and
     * {@link DialogTitleComponent}.
     * @param dialogOptions - The {@link DialogOptions} for the dialog. You can inject the data given into your component
     * via the {@link DialogReference} injection token.
     *
     * @returns An observable with the result of the dialog. It will emit one value and completes after the dialog was
     * closed. If the dialog has no result it will complete without any emit
     */
    open(componentType, dialogOptions) {
        const dialogRef = this.openWithRef(componentType, dialogOptions);
        return dialogRef.result;
    }
    /**
     * Opens a cui dialog and return the dialog. For operations like closing the dialog, you can inject {@link DialogReference} in your dialog
     * component, although it will also return the reference of the dialog. Overlay will close on backdrop click and escape key.
     *
     * @param componentType - The component type to render inside the dialog.The component must use
     * {@link DialogComponent} for consistent and correct rendering of the {@link DialogActionsComponent} and
     * {@link DialogTitleComponent}.
     * @param dialogOptions - The {@link DialogOptions} for the dialog. You can inject the data given into your component
     * via the {@link DialogReference} injection token.
     *
     * @returns The {@link CuiDialogReference} of the dialog. With it, you can perform operations like closing the dialog.
     * Also found in it, an observable with the result of the dialog. It will emit one value and completes after the dialog was
     * closed. If the dialog has no result it will complete without any emit
     */
    openWithRef(componentType, dialogOptions) {
        const overlayRef = this.overlay.create({
            positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
            hasBackdrop: true,
            panelClass: dialogOptions?.panelClass
        });
        const closingSubject = new Subject();
        const dialogRef = new CuiDialogReference(closingSubject, overlayRef, dialogOptions?.data);
        const dialogInjector = Injector.create({
            parent: this.injector,
            providers: [{
                    provide: OverlayReference,
                    useValue: overlayRef
                }, {
                    provide: DialogOptions,
                    useValue: { ...this.config.defaults, ...dialogOptions }
                }, {
                    provide: DialogReference,
                    useValue: dialogRef
                }]
        });
        const dialogPortal = new ComponentPortal(DialogPortalComponent);
        dialogPortal.injector = dialogInjector;
        const componentRef = overlayRef.attach(dialogPortal);
        const componentPortal = new ComponentPortal(componentType);
        componentPortal.injector = dialogInjector;
        componentRef.instance.attachComponentPortal(componentPortal);
        return dialogRef;
    }
}
DialogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogService, deps: [{ token: i1$3.Overlay }, { token: i0.Injector }, { token: CUI_DIALOG_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
DialogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Overlay }, { type: i0.Injector }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CUI_DIALOG_CONFIG]
                }] }]; } });

/**
 * Component for rendering additional content to the dialog title
 */
class DialogTitleComponent {
}
DialogTitleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogTitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DialogTitleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: DialogTitleComponent, selector: "cui-dialog-title", ngImport: i0, template: "<ng-content></ng-content>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogTitleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cui-dialog-title', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<ng-content></ng-content>\n" }]
        }] });

class DialogModule {
}
DialogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DialogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogModule, declarations: [DialogPortalComponent,
        DialogTitleWrapperComponent,
        DialogActionsComponent,
        DialogComponent,
        DialogTitleComponent], imports: [CommonModule,
        OverlayModule,
        PortalModule,
        IconModule,
        ButtonModule,
        A11yModule], exports: [DialogActionsComponent,
        DialogComponent,
        DialogTitleComponent] });
DialogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogModule, providers: [{
            provide: DialogService, useClass: DialogService
        }], imports: [[
            CommonModule,
            OverlayModule,
            PortalModule,
            IconModule,
            ButtonModule,
            A11yModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: DialogModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DialogPortalComponent,
                        DialogTitleWrapperComponent,
                        DialogActionsComponent,
                        DialogComponent,
                        DialogTitleComponent
                    ],
                    imports: [
                        CommonModule,
                        OverlayModule,
                        PortalModule,
                        IconModule,
                        ButtonModule,
                        A11yModule
                    ],
                    exports: [
                        DialogActionsComponent,
                        DialogComponent,
                        DialogTitleComponent
                    ],
                    providers: [{
                            provide: DialogService, useClass: DialogService
                        }]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CUI_DIALOG_CONFIG, CUI_TOAST_DEFAULT_CONFIG, CuiDialogReference, DialogActionsComponent, DialogComponent, DialogModule, DialogOptions, DialogReference, DialogService, DialogTitleComponent, DialogWidth };
